import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useState } from 'react';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import ErrorMessage from 'components/ErrorMessage';
import FundPostCard from 'components/FundPostCard';

import Button from 'primitives/Button';
import EmptyState from 'primitives/EmptyState';
import LoadingIndicator from 'primitives/LoadingIndicator';

import FundPostSlideOver from './PostSlideOver';

const FUND_POSTS_QUERY = gql(`
  query FundPostsDashboardQuery($cursor: ID, $limit: Int) {
    dashboard {
      id
      fundPosts(cursor: $cursor, limit: $limit) {
        nodes {
          id
          title
          status
          message
          createdAt
          publishedAt
          dealOfInvestment {
            id
            companyInformation {
              company {
                name
              }
            }
          }
          sender {
            id  
            name
          }
          fund {
            id
            name
            image
          }
        }
        pageInfo {
          hasNextPage
          cursor
        }
      }
    }
  }
`);

const DashboardFundPosts: FC = () => {
  const [postOnDisplay, setPostOnDisplay] = useState('');

  const { loading, error, data, refetch, fetchMore } = useQuery(FUND_POSTS_QUERY, {
    variables: {
      limit: 20,
    },
  });

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;

    if (error || !data || !data.dashboard.fundPosts)
      return <ErrorMessage error={error} refetch={refetch} />;

    const posts = data.dashboard.fundPosts.nodes;
    const pageInfo = data.dashboard.fundPosts.pageInfo;

    if (posts.length === 0)
      return (
        <EmptyState
          title="No posts received"
          description="Updates and information from Funds will appear here"
        />
      );

    return (
      <>
        <FundPostSlideOver
          open={!!postOnDisplay}
          postId={postOnDisplay}
          onClose={() => setPostOnDisplay('')}
        />
        <div className="flex flex-col">
          <div className="space-y-2 overflow-auto">
            {posts.map(post => (
              <FundPostCard
                key={post.id}
                id={post.id}
                title={post.title}
                message={post.message}
                createdAt={post.createdAt}
                deal={post.dealOfInvestment}
                sender={post.sender}
                fund={post.fund}
                publishedAt={post.publishedAt}
                onClick={() => setPostOnDisplay(post.id)}
              />
            ))}
          </div>
          {pageInfo.hasNextPage ? (
            <div className="flex justify-center p-4">
              <Button
                loading={loading}
                onClick={() =>
                  fetchMore({
                    variables: {
                      cursor: pageInfo.cursor,
                    },
                  })
                }
              >
                Load more
              </Button>
            </div>
          ) : null}
        </div>
      </>
    );
  };

  return <>{renderContent()}</>;
};

export default DashboardFundPosts;
